// src/hooks/useOnlinePresence.js

import { useEffect, useRef } from "react";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import {
  ref as rtdbRef,
  onValue,
  onDisconnect,
  set as rtdbSet,
} from "firebase/database";

import { db, rtdb } from "../firebase";

const useOnlinePresence = (user) => {
  const lastMirrorRef = useRef({ state: null, lastChanged: null });
  const firestoreEnabledRef = useRef(true);

  useEffect(() => {
    if (!user) return;

    const uid = user.uid;

    const rtdbStatusRef = rtdbRef(rtdb, `status/${uid}`);
    const connectedRef = rtdbRef(rtdb, ".info/connected");
    const firestoreUserRef = doc(db, "users", uid);

    const safeUpdateUserDoc = async (patch, label) => {
      if (!firestoreEnabledRef.current) return;

      try {
        await updateDoc(firestoreUserRef, patch);
      } catch (err) {
        const code = err?.code || "";
        if (code === "not-found") {
          console.warn(
            `[presence] updateDoc skipped (user doc not found yet) [${label}] uid=${uid}`
          );
          return;
        }
        if (code === "permission-denied") {
          console.warn(
            `[presence] permission-denied; disabling further presence writes [${label}] uid=${uid}`
          );
          firestoreEnabledRef.current = false;
          return;
        }
        console.error(`[presence] updateDoc failed [${label}] uid=${uid}`, err);
      }
    };

    console.log("[presence] init for uid:", uid);

    // Immediate fallback
    safeUpdateUserDoc(
      {
        presenceStatus: "online",
        presenceUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      "initial-online"
    );

    const unsubscribeConnected = onValue(connectedRef, (snap) => {
      const isConnected = snap.val() === true;

      if (!isConnected) {
        console.log("[presence] RTDB not connected (skip RTDB writes)");
        return;
      }

      onDisconnect(rtdbStatusRef)
        .set({ state: "offline", lastChanged: Date.now() })
        .catch((err) => console.error("[presence] onDisconnect set failed:", err));

      rtdbSet(rtdbStatusRef, { state: "online", lastChanged: Date.now() }).catch(
        (err) => console.error("[presence] RTDB set(online) failed:", err)
      );
    });

    const unsubscribeStatus = onValue(rtdbStatusRef, (snap) => {
      const val = snap.val();
      if (!val) return;

      const nextState = val.state;
      const nextChanged = val.lastChanged;

      const prev = lastMirrorRef.current;
      if (prev.state === nextState && prev.lastChanged === nextChanged) return;

      lastMirrorRef.current = { state: nextState, lastChanged: nextChanged };

      safeUpdateUserDoc(
        {
          presenceStatus: nextState,
          presenceUpdatedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(nextState === "offline" ? { lastSeen: serverTimestamp() } : {}),
        },
        `mirror-${nextState}`
      );
    });

    const handleUnload = () => {
      safeUpdateUserDoc(
        {
          presenceStatus: "offline",
          presenceUpdatedAt: serverTimestamp(),
          lastSeen: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        "beforeunload-offline"
      );
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      console.log("[presence] cleanup for uid:", uid);
      safeUpdateUserDoc(
        {
          presenceStatus: "offline",
          presenceUpdatedAt: serverTimestamp(),
          lastSeen: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        "cleanup-offline"
      );
      unsubscribeConnected();
      unsubscribeStatus();
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [user]);
};

export default useOnlinePresence;
